import { AlertTriangle, Building2, Clock, Mail, MapPin, Phone, RotateCcw } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';

import type { SituacaoDaBuscaDeTitular } from './contrato';

type SituacaoDeOrientacao = Exclude<SituacaoDaBuscaDeTitular, 'encontrado'>;

interface MensagemDeOrientacao {
  titulo: string;
  descricao: string;
}

const MENSAGENS: Record<SituacaoDeOrientacao, MensagemDeOrientacao> = {
  nao_encontrado: {
    titulo: 'CPF não encontrado no cadastro do DTP',
    descricao:
      'Não localizamos nenhum titular com este CPF no cadastro do DTP. Confira o número digitado ou oriente o titular a regularizar o cadastro presencialmente antes de tentar de novo.',
  },
  sem_email: {
    titulo: 'Titular sem e-mail cadastrado',
    descricao:
      'O titular foi localizado, mas não há e-mail associado a este CPF no cadastro do DTP. Sem um e-mail válido não é possível notificá-lo sobre esta inscrição — oriente-o a atualizar o cadastro antes de prosseguir.',
  },
  sem_telefone: {
    titulo: 'Titular sem telefone cadastrado',
    descricao:
      'O titular foi localizado, mas não há telefone associado a este CPF no cadastro do DTP. Sem um telefone válido não é possível notificá-lo sobre esta inscrição — oriente-o a atualizar o cadastro antes de prosseguir.',
  },
  sem_licenca_ativa: {
    titulo: 'Titular sem licença ativa',
    descricao:
      'O titular foi localizado, mas não há licença ativa vinculada a este CPF no cadastro do DTP. É preciso regularizar a licença antes de concluir esta inscrição.',
  },
};

/**
 * Orientação exibida quando a busca do titular no cadastro externo não
 * permite seguir direto para a inscrição — CPF não localizado, ou falta
 * e-mail, telefone ou licença ativa. Só usada nesta tela: não é um problema
 * compartilhado com o resto do portal, então não vira componente de
 * `../comum/`.
 */
export function OrientacaoDtpAlert({
  situacao,
  onTentarOutroCpf,
}: {
  situacao: SituacaoDeOrientacao;
  onTentarOutroCpf: () => void;
}) {
  const mensagem = MENSAGENS[situacao];

  return (
    <Alert className="border-warning/40 bg-warning/5">
      <AlertTriangle className="h-5 w-5 text-warning" aria-hidden="true" />
      <AlertTitle className="text-foreground">{mensagem.titulo}</AlertTitle>
      <AlertDescription className="space-y-3">
        <p className="text-foreground/90 text-sm">{mensagem.descricao}</p>

        <div className="border-warning/30 bg-background/60 space-y-1.5 rounded-lg border p-3">
          <p className="text-foreground flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
            <Building2 className="h-3.5 w-3.5" aria-hidden="true" />
            Atendimento do DTP
          </p>
          <ul className="text-foreground/90 space-y-1 text-xs">
            <li className="flex items-start gap-2">
              <MapPin
                className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              Rua Líbero Badaró, 425 — Centro, São Paulo/SP
            </li>
            <li className="flex items-start gap-2">
              <Phone
                className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              (11) 3396-8100
            </li>
            <li className="flex items-start gap-2">
              <Mail
                className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              atendimento.dtp@prefeitura.sp.gov.br
            </li>
            <li className="flex items-start gap-2">
              <Clock
                className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0"
                aria-hidden="true"
              />
              Segunda a sexta, das 8h às 17h
            </li>
          </ul>
        </div>

        <Button variant="outline" size="sm" onClick={onTentarOutroCpf} className="gap-2">
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          Tentar outro CPF
        </Button>
      </AlertDescription>
    </Alert>
  );
}
